import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import {
  boardState,
  BoardStatus,
  setStatus,
  setPlanId
} from 'common/modules/app/board';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';
import { AppState } from 'pages';
import { BeforePurchaseBoard } from 'pages/board/components/BeforePurchaseBoard';
import { UserInformation } from 'common/modules/domain/user';
import { PlanInitialState } from 'common/modules/domain/plan';
import {
  PurchaseSessionState,
  setPurchaseState
} from 'common/modules/domain/purchaseSession';
import {
  POST_PURCHASE_SESSION_ID,
  PurchaseStateType
} from 'common/modules/domain/purchaseSession';
import { SasPagePath, sasStaticUrl } from 'common/utils/urls';
import {
  InnerError,
  purchaseSessionAlreadyContracted,
  purchaseSessionDuplicatePurchased
} from 'common/modules/app/innerErrorCode';
import {
  alreadyContracted,
  partiallyDuplicated,
  purchaseError,
  purchasesAdditional,
  duplicatePurchase,
  duplicatePurchased
} from 'common/modules/app/errors';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const planState = useSelector<AppState, PlanInitialState>(
    (state) => state.domain.plan
  );
  const purchaseState = useSelector<AppState, PurchaseSessionState>(
    (state) => state.domain.purchaseSession
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const targetPlan = planState.planList[boardState.selectedPlanId];

  return {
    isDisplay: boardState.status === BoardStatus.BEFORE_PURCHASE_DISP,
    targetPlan: targetPlan,
    country: userState.country,
    language: userState.language,
    purchase: purchaseState,
    glossary: Glossary.instance(glState)
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
// Actionをdispatchする関数からComponentに渡すPropsへ
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();

  /** クローズボタンの押下処理 */
  const onClickClose = () => {
    dispatch(setPlanId(''));
    dispatch(setStatus(BoardStatus.NOT_DISP));
  };

  /** "利用規約を確認"の押下処理 */
  const onClickTermsLink = () => {
    // 利用規約を別タブで開く
    const html = state.targetPlan
      ? '/st/term-of-use.html?planId=' + state.targetPlan.planId
      : '/st/term-of-use.html';
    window.open(`${sasStaticUrl}${html}`);
  };

  /** "購入手続きへ進む"の押下処理 */
  const onClickMoveToPurchase = async () => {
    if (state.purchase.state == PurchaseStateType.TYPE_PURCHASE) {
      // stateが"purchase"ならば決済画面を表示
      await dispatch(setPurchaseState(PurchaseStateType.TYPE_DISPLAY_PAYMENT));
    } else {
      if (state.purchase.state == PurchaseStateType.TYPE_PARTIAL) {
        // stateが"partial"の場合は一部重複警告画面へ遷移
        const error = InnerError.purchaseSessionPartiallyDuplicated;
        error.requestId = POST_PURCHASE_SESSION_ID;
        dispatch(partiallyDuplicated(error));
      } else if (state.purchase.state == PurchaseStateType.TYPE_ADDITIONAL) {
        // stateが"additional"の場合は追加購入警告画面へ遷移
        const error = InnerError.purchaseSessionAdditionalError;
        error.requestId = POST_PURCHASE_SESSION_ID;
        dispatch(purchasesAdditional(error));
      } else if (state.purchase.state == PurchaseStateType.TYPE_PURCHASED) {
        // stateが"purcahsed"の場合は多重購入エラー画面へ遷移
        const error = purchaseSessionAlreadyContracted(state.targetPlan.name);
        error.requestId = POST_PURCHASE_SESSION_ID;
        dispatch(alreadyContracted(error));
      } else if (
        state.purchase.state == PurchaseStateType.TYPE_DUPLICATE_PURCHASE
      ) {
        // stateが"duplicate_purchaseの場合は重複購入可能画面へ遷移
        const error = InnerError.purchaseSessionDuplicatePurchase;
        error.requestId = POST_PURCHASE_SESSION_ID;
        dispatch(duplicatePurchase(error));
      } else if (
        state.purchase.state == PurchaseStateType.TYPE_DUPLICATE_PURCHASED
      ) {
        // stateが"duplicate_purchasedの場合は重複購入不可画面へ遷移
        const error = purchaseSessionDuplicatePurchased(state.targetPlan.name);
        error.requestId = POST_PURCHASE_SESSION_ID;
        dispatch(duplicatePurchased(error));
      } else {
        // 上記以外は購入手続きエラー画面へ遷移
        const error = InnerError.purchaseSessionPurchaseError;
        error.requestId = POST_PURCHASE_SESSION_ID;
        dispatch(purchaseError(error));
      }
      // エラーまたは警告画面へ遷移
      dispatch(push(SasPagePath.Error));
    }
    // 購入確認画面を閉じる
    dispatch(setStatus(BoardStatus.NOT_DISP));
  };

  return {
    onClickClose: onClickClose,
    onClickTermsLink: onClickTermsLink,
    onClickMoveToPurchase: onClickMoveToPurchase
  };
};

/**
 * 購入確認画面全体のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  React.useEffect(() => {
    const payment = _props.targetPlan
      ? _props.targetPlan.prices[0].payment
      : '';
    if (
      _props.purchase.purchaseUrl &&
      _props.purchase.state === PurchaseStateType.TYPE_DISPLAY_PAYMENT &&
      payment === 'clever-bridge'
    ) {
      // Cleberbridgeの決済画面を同タブ表示
      window.location.href = `${_props.purchase.purchaseUrl}`;
    }
  }, [_props.purchase]);

  return <BeforePurchaseBoard {..._props} />;
}
